import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0309 from "../lesson-09/lesson";
const linkLesson0309 = getLinkForCourseLesson("49817b85-0b30-4661-8a25-1e5176041865", lesson0309.id);

const theory = `<h3 class=likesectionHead>Introduction</h3>
<p>In the field of acoustics, a room is often primarily characterized by its reverberation. If in a room a sound source is suddenly switched off, the sound energy in the room does not instantly disappear, but decays over time. This is caused by sound waves, which still propagate in the room and are reflected on walls gradually loosing their energy. In order to predict this energy decay, it is not necessary to conduct a geometrical propagation simulation using an image source model or a ray tracing. The decay of the sound pressure level can be described by the following equation.
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mi>L</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>4</mn>
            <mn>34</mn>
            <mo class=MathClass-bin>⋅</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>cS</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>V</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>⋅</mo>
            <mi mathvariant=italic>ln</mi>
            <mo class=MathClass-open>(</mo>
            <mn>1</mn>
            <mo class=MathClass-bin>−</mo>
            <mi>α</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>⋅</mo>
            <mi>t</mi>
            <mstyle mathvariant=normal>
               <mo class=MathClass-open>(</mo>
               <mi>dB</mi>
               <mo class=MathClass-close>)</mo>
            </mstyle>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   The reverberation
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   is defined as the time required for a sound level to decrease by 60 dB. Based on this statistical reverberation theory, the reverberation time can be easily predicted using rather simple equations.
<h3 class=likesectionHead>Sabine’s reverberation time equation</h3>
<p>The start of the studies on architectural acoustics and how it affects an individual’s listening experience, was started in the last years of the 19th century by Wallace Clement Sabine [3]. He was the first researcher to make quantitative acoustical measurements in a hall with the aim to create solutions for the poor acoustics in the Fogg Museum’s lecture hall in Cambridge, Massachusetts. Sabine conducted a series of experiments investigating how the room’s variables (shape, and wall materials) effect the reverberation and the sound quality in the space. He was able to calculate the rate of sound decay in a room by precisely measuring the inverse, the duration of the audibility of the decaying sound. These measurements were carried out with a constant sound produced by an organ pipe that would cease to play at some point, and a chronograph. At this late stage of the lecture hall’s design process the only variable that could be changed was the materials covering every surface. The empty room was evaluated first and then the experiment was repeated changing just one material to size its impact in the sound field once introduced. Based on this data, an empirical formula could be developed: the so-called Sabine equation (Eq. 2),
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mi>T</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>0</mn>
            <mn>161</mn>
            <mo class=MathClass-bin>⋅</mo>
            <mfrac>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>A</mi>
               </mrow>
            </mfrac>
            <mstyle mathvariant=normal>
               <mo class=MathClass-open>(</mo>
               <mi>s</mi>
               <mo class=MathClass-close>)</mo>
            </mstyle>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>V</mi>
   </math>
   being the room’s volume in
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>3</mn>
         </mrow>
      </msup>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>A</mi>
   </math>
   its equivalent absorption area that can be calculated as follows (Eq. 3).
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mi>A</mi>
            <mo class=MathClass-rel>=</mo>
            <mi>S</mi>
            <mover accent=true>
               <mrow>
                  <mi>α</mi>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
            <mo class=MathClass-rel>=</mo>
            <mover class=msup>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </mover>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>α</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mstyle mathvariant=normal>
               <mo class=MathClass-open>(</mo>
               <msup>
                  <mrow>
                     <mi>m</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
               <mo class=MathClass-close>)</mo>
            </mstyle>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   refer to the total surface of the room and to each material’s surface, respectively;
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>α</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   is the absorption coefficient of wall
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>i</mi>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>α</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>
   denotes the mean absorption coefficient for all walls of the room.
<p>
   The result obtained by the Sabine equation is an estimation of the reverberation time for the frequency band that the absorption coefficients (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>α</mi>
   </math>
   ) refer to. Generally, it is computed for each octave or each third octave band of the spectrum. Using this equation, it can be calculated how the sound decays at different rates for different frequency bands (as it is demonstrated in the principle section).
<p>It should be noted that the air absorption, especially in large halls and towards higher frequencies, will play a major role in damping the emitted acoustical energy (as it can be observed in the principle part). Therefore, to obtain more accurate results a correcting term is added to the Eq. 2 resulting in the formula below (Eq. 4).
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mi>T</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>0</mn>
            <mn>161</mn>
            <mo class=MathClass-bin>⋅</mo>
            <mfrac>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>A</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mn>4</mn>
                  <mi mathvariant=italic>mV</mi>
               </mrow>
            </mfrac>
            <mstyle mathvariant=normal>
               <mo class=MathClass-open>(</mo>
               <mi>s</mi>
               <mo class=MathClass-close>)</mo>
            </mstyle>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   The relevance of air absorption depends on the air temperature, the relative humidity, and as previously mentioned on the frequency. Some example values for the air attenuation coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>m</mi>
   </math>
   are listed in <a href="#" data-img-alt="Table 1. Different air absorption coefficients [
    <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
       <mfrac>
          <mrow>
             <mn>1</mn>
             <msup>
                <mrow>
                   <mn>0</mn>
                </mrow>
                <mrow>
                   <mo class=MathClass-bin>−</mo>
                   <mn>3</mn>
                </mrow>
             </msup>
          </mrow>
          <mrow>
             <mi>m</mi>
          </mrow>
       </mfrac>
    </math>
    ] at 20<span class=tcrm-1000>º </span>C and standard air pressure (101.325 kPa) and for different relative humidity (RH) values." data-img-src=${config.ASSETS_URL}/03/07/theory/table-1.svg>Table 1</a>.
<div class=table>
   <figure class=float>
      <figcaption class=caption>
         Table 1. Different air absorption coefficients [
         <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mfrac>
               <mrow>
                  <mn>1</mn>
                  <msup>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mn>3</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </mfrac>
         </math>
         ] at 20<span class=tcrm-1000>º </span>C and standard air pressure (101.325 kPa) and for different relative humidity (RH) values.
      </figcaption>
      <img alt="Table 1. Different air absorption coefficients [
        <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
           <mfrac>
              <mrow>
                 <mn>1</mn>
                 <msup>
                    <mrow>
                       <mn>0</mn>
                    </mrow>
                    <mrow>
                       <mo class=MathClass-bin>−</mo>
                       <mn>3</mn>
                    </mrow>
                 </msup>
              </mrow>
              <mrow>
                 <mi>m</mi>
              </mrow>
           </mfrac>
        </math>
        ] at 20<span class=tcrm-1000>º </span>C and standard air pressure (101.325 kPa) and for different relative humidity (RH) values." src=${config.ASSETS_URL}/03/07/theory/table-1.svg>
   </figure>
</div>
<p>
   By accounting for the air absorption the problem of very large reverberation times for very low values of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>α</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>
   is fixed, however, the equation does also not lead to a reverberation time of zero for the other extreme case, when
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>α</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>
   .
<h3 class=likesectionHead>Eyring’s reverberation time equation</h3>
<p>Sabine’s equation works best when predicting reverberation times in low absorption rooms. If it is desired to estimate the reverberation time somewhere with higher overall absorption, a similar equation exists that will provide, in most cases, more accurate results, the so-called Eyring equation (Eq. 5).
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mi>T</mi>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mn>0</mn>
            <mn>161</mn>
            <mfrac>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>S</mi>
                  <mo class=MathClass-bin>⋅</mo>
                  <mi mathvariant=italic>ln</mi>
                  <mo class=MathClass-open>(</mo>
                  <mn>1</mn>
                  <mo class=MathClass-bin>−</mo>
                  <mover accent=true>
                     <mrow>
                        <mi>α</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>+</mo>
                  <mn>4</mn>
                  <mi mathvariant=italic>mV</mi>
               </mrow>
            </mfrac>
            <mstyle mathvariant=normal>
               <mo class=MathClass-open>(</mo>
               <mi>s</mi>
               <mo class=MathClass-close>)</mo>
            </mstyle>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   To derive this formula, it is assumed that the sound field is composed by plane waves which lose energy over time after each reflection. The remaining energy of wave with initial unit energy after the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>n</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>th</mi>
         </mrow>
      </msup>
   </math>
   reflection would be
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mo class=MathClass-open>(</mo>
            <mn>1</mn>
            <mo class=MathClass-bin>−</mo>
            <mover accent=true>
               <mrow>
                  <mi>α</mi>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
            <mo class=MathClass-close>)</mo>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msup>
   </math>
   . Analog to the Sabine equation, the equation can be corrected for air absorption in the same way as the Sabine equation.
<p>
   For lower absorption coefficients (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>α</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mo class=MathClass-rel><</mo>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>1</mn>
   </math>
   ) both equations produce similar results, but starting from a mean room absorption
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>α</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mo class=MathClass-rel>≈</mo>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>3</mn>
   </math>
   the results will start to diverge, with the Sabine equation systematically outputting higher values than Eyring equation. When approaching high absorption values (close to one) Eyring’s formula leads to a more reasonable result (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>0</mn>
   </math>
   s), while Sabine’s formula outputs a constant time of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>161</mn>
      <mfrac>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>S</mi>
         </mrow>
      </mfrac>
   </math>
   .<br class=newline>
<p>Even today, both equations are very popular and are still used for room acoustic design. However, it should be considered that both equations do not deliver valid results in case of inhomogeneously distributed absorption in the room. This is because the both statistical equations assume diffuse field conditions, implying that all absorption is homogeneously distributed on all surfaces.
<h3 class=likesectionHead>Other prediction models</h3>
<h4 class=likesubsectionHead>Equations for room parameters</h4>
<p>
   In addition to Sabine’s and Eyring’s equations, other formulas have been developed in order to predict the reverberation time more accurately and get closer to reality. Examples are the <em><span class=cmti-10>Millington-Sette</span></em> equation or an approach described in the EN 12354 standard [1] which is intended as a better predictor in case of a non-uniform distribution of absorption in the room.<br class=newline>For other room acoustic parameters, no simple equations exist to obtain values based on basic characteristics of the room. For the parameter clarity, however, it is possible to derive a value based on the reverberation time
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   , if the energy decay follows a pure exponential decay (corresponding to a linear decay of the sound pressure level expressed in dB, as in Eq. 1) [2].
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>exp</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>10</mn>
            <msub>
               <mrow>
                  <mi class=qopname>log</mi>
                  <mo>⁡</mo>
               </mrow>
               <mrow>
                  <mn>10</mn>
               </mrow>
            </msub>
            <mrow>
               <mo form=prefix fence=true>[</mo>
               <mrow>
                  <mi class=qopname>exp</mi>
                  <mo>⁡</mo>
                  <mrow>
                     <mo form=prefix fence=true>(</mo>
                     <mrow>
                        <mfrac>
                           <mrow>
                              <mn>1</mn>
                                                <mn>104</mn>
                           </mrow>
                           <mrow>
                              <mi>T</mi>
                           </mrow>
                        </mfrac>
                     </mrow>
                     <mo form=postfix fence=true>)</mo>
                  </mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mn>1</mn>
               </mrow>
               <mo form=postfix fence=true>]</mo>
            </mrow>
            <mstyle mathvariant=normal>
               <mo class=MathClass-open>(</mo>
               <mi>dB</mi>
               <mo class=MathClass-close>)</mo>
            </mstyle>
         </math>
      <td class=eq-no>(6)
</table>
<p>Similarly, the room acoustic parameter strength can be calculated using Eq. 7.
<table class=equation>
   <tr>
      <td style="font-size: 15px">
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <msub>
               <mrow>
                  <mi>G</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>exp</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>10</mn>
            <msub>
               <mrow>
                  <mi class=qopname>log</mi>
                  <mo>⁡</mo>
               </mrow>
               <mrow>
                  <mn>10</mn>
               </mrow>
            </msub>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi>T</mi>
                     </mrow>
                     <mrow>
                        <mi>V</mi>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
            <mo class=MathClass-bin>+</mo>
            <mn>45</mn>
            <mstyle mathvariant=normal>
               <mo class=MathClass-open>(</mo>
               <mi>dB</mi>
               <mo class=MathClass-close>)</mo>
            </mstyle>
         </math>
      <td class=eq-no>(7)
</table>
<p>While it might be reasonable to obtain an average clarity or strength value for a room, it should be noted that these two parameters are generally highly depending on the position, especially the distance from receiver to the source.
<h4 class=likesubsectionHead>Simulation models</h4>
<p>A more complex but also more reliable way of approaching the problem of how to compute the reverberation time (and other parameters) of a room, would be using an acoustic simulation software. They work by recreating the room with a 3D computer-aided design (CAD) model. There are three main aspects to these types of simulations: the general shape of the room, the materials assigned to each surface in the model, and the position of both source and receiver.<br class=newline>
<p>Based on this data, the energy decay of the room is calculated. Usually, this is achieved by tracing acoustical rays from source to receiver through every possible path (taking into account reflections, and occasionally scattering and diffraction). The simulation of each ray will run until the particle has reached the receiver (only with some methods), it has run out of energy, or it has reached a maximum run-time. The detected particles are counted in an energy histogram, which then can be evaluated to obtain the reverberation time and other room parameters of interest. More details on this method are presented in <a href="${linkLesson0309}" target="_blank">Lesson 3.9</a>.
<h4 class=likesubsectionHead>Scale model measurements</h4>
<p>Building and simulating the sound field in a scale model of the room is another approach that can be taken to be able to predict energy decay or its impulse response. It is obviously a way more time consuming method than a computer simulation but it is still regarded, especially for rooms that present more intricate geometries, as a more accurate approach.<br class=newline>
<p>One of the major advantages of scale models over computer simulations is that scale models fully take into account how objects and surfaces interact with waves, which is dependent on their relative size. A fairly small object that is in the path of propagation of a low frequency wave will be “invisible” to it; while the same body will become an impossible obstacle to surround for shorter wavelengths. Whenever the relative sizes between wave and object are comparable, there will be a mixture of both interaction models, the wave will be diffracted, scattered, and reflected by this object. Thus, special care must be put both into the scaling of frequencies (physically scaling their wavelength) and choosing the right materials.<br class=newline>
<p>
   To calculate the spectrum that should be emitted in the scale model it must be shifted by the scale factor
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>M</mi>
   </math>
   , the model’s scale factor
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <mo class=MathClass-punc>:</mo>
      <mi>M</mi>
   </math>
   . This leads to an increase of applied frequencies
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
          </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mi>f</mi>
      <mo class=MathClass-bin>⋅</mo>
      <mi>M</mi>
   </math>
   , requiring loudspeakers and microphones which can handle these high frequencies. The materials chosen for the model should work analogously to the real materials, they should have similar acoustical properties to those the original surfaces would have in the base spectrum (such that,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>α</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
          </mrow>
      </msubsup>
      <mo class=MathClass-open>(</mo>
      <msup>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
          </mrow>
      </msup>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>α</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>f</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   ). In practice this part of the process could be simplified by grouping into mainly reflective (such as plywood or concrete) and mainly absorbent materials (such as seats and audience). The latter should be modeled with special care so that effects such as head diffraction and absorption are correctly portrayed in the resulting impulse response. Another issue that arises from using higher frequencies than those the room would be intended to have is the air absorption, which increases with the sound’s frequency. In practice this can be solved by a using a filter to compensate the absorbed energy, by drying up the air that the model contains (as seen in <a href="#" data-img-alt="Table 1. Different air absorption coefficients [
    <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
       <mfrac>
          <mrow>
             <mn>1</mn>
             <msup>
                <mrow>
                   <mn>0</mn>
                </mrow>
                <mrow>
                   <mo class=MathClass-bin>−</mo>
                   <mn>3</mn>
                </mrow>
             </msup>
          </mrow>
          <mrow>
             <mi>m</mi>
          </mrow>
       </mfrac>
    </math>
    ] at 20<span class=tcrm-1000>º </span>C and standard air pressure (101.325 kPa) and for different relative humidity (RH) values." data-img-src=${config.ASSETS_URL}/03/07/theory/table-1.svg>Table 1</a>, the air absorption is directly related to the humidity), or by replacing the model’s air with nitrogen.
<h3 class=likesectionHead>Glossary</h3>
<dl>
  <dt>CAD
  <dd>Computer-Aided Design
</dl>
<h3 class=sectionHead>References</h3>
  <dl class=thebibliography>
    <dt>[1]
    <dd>EN 12354-6. Building acoustics – Estimation of acoustic performance of buildings from the performance of elements – Part 6: sound absorption in enclosed spaces. Beuth, Berlin, Germany, 2003.
    <dt>[2]
    <dd>T. Rossing. Springer Handbook of Acoustics. Springer Handbook of Acoustics. Springer New York, 2007.
    <dt>[3]
    <dd>W. C. Sabine. Collected papers on acoustics. Books on Architecture. Havard University Press, Cambridge, 1922.
  </dl>`;

export default theory;
